import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Scale,
  Users,
  Calendar,
  UserRound,
  ListChecks,
  FileText,
  Sparkles,
  Wallet,
  Settings,
  LogOut,
  Phone,
  Calculator,
  Gavel,
  BarChart3,
  Receipt,
  type LucideIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

/** `public/` — manter em sync com `imgs/logos allana/` quando atualizar arte */
const LETTER_A_LOGO_SRC = "/imgs/logos allana/letra_A_logo.png";

interface AppSidebarProps {
  userRole: "CEO" | "Advogado" | "Estagiário" | "Secretário(a)";
  userName: string;
}

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  /** Outras rotas que contam como esta seção (ex.: página de um processo) */
  also?: string[];
  available?: boolean;
}

export function AppSidebar({ userRole, userName }: AppSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed" && !isMobile;
  const { logout, profile } = useAuth();

  const groups: { label: string; items: NavItem[] }[] = [
    {
      label: "Trabalho",
      items: [
        { icon: Home, label: "Início", path: "/" },
        { icon: Scale, label: "Processos", path: "/cases", also: ["/processo/", "/administrative-process/", "/archived-cases"] },
        { icon: Gavel, label: "Intimações", path: "/intimacoes" },
        { icon: Users, label: "Clientes", path: "/clients" },
        { icon: Calendar, label: "Agenda", path: "/agenda" },
        { icon: UserRound, label: "Atendimentos", path: "/appointments" },
        { icon: ListChecks, label: "Tarefas", path: "/tasks" },
      ],
    },
    {
      label: "Escritório",
      items: [
        { icon: Wallet, label: "Financeiro", path: "/financial", available: userRole === "CEO" },
        { icon: BarChart3, label: "Relatórios", path: "/reports" },
        { icon: Receipt, label: "Honorários", path: "/honorarios" },
        { icon: FileText, label: "Documentos", path: "/documents" },
        { icon: Phone, label: "Contatos", path: "/contacts" },
      ],
    },
    {
      label: "Ferramentas",
      items: [
        { icon: Sparkles, label: "Centro IA", path: "/ai-center" },
        { icon: Calculator, label: "Cálculos e modelos", path: "/legal-tools" },
      ],
    },
  ];

  const isActive = (item: NavItem) => {
    const path = location.pathname;
    if (item.path === "/") return path === "/";
    return path === item.path || path.startsWith(`${item.path}/`) || (item.also || []).some((p) => path.startsWith(p));
  };

  const go = (path: string) => {
    navigate(path);
    if (isMobile) setOpenMobile(false);
  };

  const displayName = profile?.full_name || userName || "Usuária";
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-white/[0.06] bg-[linear-gradient(165deg,hsl(220_14%_10%)_0%,hsl(220_18%_6%)_45%,hsl(220_22%_4%)_100%)] text-zinc-100 shadow-[4px_0_24px_-8px_rgba(0,0,0,0.45)]"
    >
      <SidebarRail className="after:bg-white/10 hover:after:bg-[#c4ab83]/40" />

      {/* Marca */}
      <SidebarHeader className={cn("border-b border-white/[0.06]", isCollapsed ? "px-1.5 py-3" : "px-4 py-4")}>
        <button type="button" onClick={() => go("/")} className={cn("flex items-center gap-3 text-left", isCollapsed && "justify-center")}>
          <img src={LETTER_A_LOGO_SRC} alt="" className={cn("shrink-0 object-contain drop-shadow-[0_2px_14px_rgba(212,175,55,0.28)]", isCollapsed ? "h-9 w-9" : "h-10 w-10")} />
          {!isCollapsed && (
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-[13px] font-semibold tracking-[0.12em] text-zinc-100">ALLANA STHÉFANY</span>
              <span className="block truncate text-[11px] text-[#c4ab83]">Advocacia & Consultoria</span>
            </span>
          )}
        </button>
      </SidebarHeader>

      {/* Navegação */}
      <SidebarContent className="scrollbar-hide gap-0 px-2 py-2">
        {groups.map((group) => (
          <SidebarGroup key={group.label} className="py-1.5">
            <SidebarGroupLabel className="px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {group.items
                  .filter((item) => item.available !== false)
                  .map((item) => {
                    const active = isActive(item);
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          onClick={() => go(item.path)}
                          isActive={active}
                          tooltip={item.label}
                          className={cn(
                            "relative h-9 rounded-xl text-[13px] font-medium text-zinc-400 shadow-none transition-all duration-200",
                            "hover:bg-white/[0.06] hover:text-zinc-100",
                            active && "!bg-white/[0.08] !text-zinc-50 font-semibold"
                          )}
                        >
                          {active && !isCollapsed && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[#c4ab83]" />}
                          <item.icon className={cn("!size-[1.05rem] shrink-0 stroke-[1.75]", active ? "text-[#d9c49a]" : "text-zinc-500")} />
                          <span className="truncate">{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* Perfil, configurações e sair */}
      <SidebarFooter className="border-t border-white/[0.06] p-2">
        {isCollapsed ? (
          <SidebarMenu className="gap-0.5">
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => go("/profile")} tooltip={displayName} className="h-10 justify-center rounded-xl hover:bg-white/[0.06]">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={profile?.avatar_url || ""} className="object-cover" />
                  <AvatarFallback className="bg-[#c4ab83]/20 text-[10px] font-semibold text-[#7a6440]">{initials}</AvatarFallback>
                </Avatar>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={logout} tooltip="Sair" className="h-9 rounded-xl text-rose-400/90 hover:bg-rose-500/15 hover:text-rose-300">
                <LogOut className="!size-[1.05rem]" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : (
          <div className="flex items-center gap-2 rounded-xl px-1 py-1">
            <button type="button" onClick={() => go("/profile")} className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl p-1.5 text-left transition-colors hover:bg-white/[0.06]">
              <Avatar className="h-9 w-9 border border-[#c4ab83]/40">
                <AvatarImage src={profile?.avatar_url || ""} className="object-cover" />
                <AvatarFallback className="bg-[#c4ab83]/20 text-xs font-semibold text-[#7a6440]">{initials}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 leading-tight">
                <span className="block truncate text-[13px] font-semibold text-zinc-100">{displayName}</span>
                <span className="block truncate text-[11px] text-zinc-500">{userRole === "CEO" ? "Advogada · CEO" : userRole}</span>
              </span>
            </button>
            <button type="button" onClick={() => go("/settings")} title="Configurações" className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-200">
              <Settings className="h-4 w-4" />
            </button>
            <button type="button" onClick={logout} title="Sair" className="rounded-lg p-2 text-rose-400/80 transition-colors hover:bg-rose-500/15 hover:text-rose-300">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
